import React, { useState } from "react";
import { api } from "../../lib/api";
import { useI18n } from "../../lib/i18n";
import { AtelierModal } from "./AtelierModal";
import { PillCTA, SmallCaps } from "./primitives";
import { analytics } from "../../lib/analytics";

/**
 * Concierge request modal (M9). Prefills auth user's identity server-side.
 *
 * Its copy used to be English-only, which was survivable while the only way in
 * was a button on the product page. The homepage band now offers it to every
 * visitor, most of whom are reading the site in Azerbaijani, so the strings go
 * through the same locale picker as the rest of the storefront.
 */
export function ConciergeModal({ open, onClose, productHandle, productTitle }) {
  const { locale } = useI18n();
  const t = (az, en) => (locale === "az" ? az : en);
  const [message, setMessage] = useState("");
  const [phone, setPhone] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const submit = async () => {
    setSubmitting(true); setError(null);
    try {
      const r = await api.post("/concierge/requests", {
        product_handle: productHandle,
        message,
        phone: phone || undefined,
        page_url: typeof window !== "undefined" ? window.location.href : undefined,
      });
      setResult(r.data);
      analytics.lead("concierge", productHandle ? { handle: productHandle } : null);
    } catch (e) {
      setError(e?.response?.data?.detail
        || t("Atelye ilə əlaqə qurulmadı.", "Unable to reach the atelier."));
    } finally { setSubmitting(false); }
  };

  const reset = () => { setResult(null); setMessage(""); setPhone(""); };

  return (
    <AtelierModal
      open={open}
      onClose={() => { onClose?.(); setTimeout(reset, 400); }}
      title={t("Konsyerj sorğusu", "Request concierge")}
      subtitle={productTitle
        ? t(`${productTitle} üçün şəxsi dəstək`, `Private assistance for ${productTitle}`)
        : t("Atelyenin stilisti ilə danışın", "Speak with your atelier stylist")}
      testid="concierge-modal"
    >
      {!result ? (
        <div className="space-y-6">
          <label className="block">
            <span className="text-[11px] uppercase tracking-widest text-graphite/75">{t("Telefon (istəyə bağlı)", "Phone (optional)")}</span>
            <input
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="+994 …"
              data-testid="concierge-phone"
              className="mt-2 w-full bg-transparent border-b border-graphite/40 focus:border-espresso focus:outline-none py-2 font-sans"
            />
          </label>
          <label className="block">
            <span className="text-[11px] uppercase tracking-widest text-graphite/75">{t("Qeydiniz", "Your note")}</span>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={5}
              placeholder={t(
                "Ölçü, alternativ rəng və ya şəxsi baxış barədə soruşun…",
                "Ask about fit, an alternative colour, private viewing…",
              )}
              data-testid="concierge-message"
              className="mt-2 w-full bg-transparent border-b border-graphite/40 focus:border-espresso focus:outline-none py-2 font-sans resize-none"
            />
          </label>
          {error && <div className="text-red-800/80 text-sm">{error}</div>}
          <div className="pt-4">
            <PillCTA
              variant="primary"
              onClick={submit}
              disabled={submitting}
              data-testid="concierge-submit"
              className="w-full"
            >
              {submitting ? t("Göndərilir…", "Sending…") : t("Atelyeyə göndər", "Send to the atelier")}
            </PillCTA>
          </div>
          <p className="text-[11px] uppercase tracking-widest text-graphite/70">
            {t(
              "Gold Gentleman və yuxarı səviyyələr üçün sorğular önə keçir.",
              "Gold Gentleman & above receive priority routing.",
            )}
          </p>
        </div>
      ) : (
        <div data-testid="concierge-result" className="space-y-6">
          <div className="border border-espresso/50 bg-stone/20 p-6">
            <SmallCaps>{t("Qəbul edildi", "Received")}</SmallCaps>
            <p className="font-serif italic text-xl mt-3 leading-snug">
              {t("Stilist tezliklə sizinlə əlaqə saxlayacaq.", "A stylist will be in touch shortly.")}{" "}
              {result.priority ? t("Sorğunuz önə keçirildi.", "Priority routing applied.") : ""}
            </p>
          </div>
          {result.whatsapp_url && (
            <div className="border border-graphite/20 p-6">
              <SmallCaps>{t("WhatsApp qısayolu", "WhatsApp shortcut")}</SmallCaps>
              <p className="mt-3 text-sm text-graphite/75 font-light">
                {t(
                  "WhatsApp-da danışmaq istəyirsiniz? Atelye ilə hazır söhbəti açın:",
                  "Prefer to speak on WhatsApp? Open a pre-filled conversation with the atelier:",
                )}
              </p>
              <a
                href={result.whatsapp_url}
                target="_blank"
                rel="noreferrer"
                data-testid="concierge-whatsapp-link"
                className="atelier-link text-[11px] uppercase tracking-widest text-espresso mt-4 inline-block"
              >
                {t("WhatsApp-ı aç", "Open WhatsApp")} →
              </a>
              <pre className="mt-4 whitespace-pre-wrap text-[12px] text-graphite/75 font-sans">
                {result.whatsapp_prefilled_message}
              </pre>
            </div>
          )}
        </div>
      )}
    </AtelierModal>
  );
}
